import React from 'react';

import dayjs from '~/timeInit';

import { Box, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import DateWidget from '~/components/Common/Elements/DateWidget';

export type RankEdit = {
  override: string;
  overrideReason: string;
  overrideUntil: string;
};

function formatDateTime(value?: string): string {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
}

interface Props {
  label: string;
  rank?: string;
  updated?: string;
  overrideUpdated?: string;
  edit: RankEdit;
  overrideOptions: Array<string>;
  currentUserName?: string;
  onChange: (edit: RankEdit) => void;
}

function appendUserNameToReason(reason: string, currentUserName?: string): string {
  const trimmedReason = reason.trim();
  const trimmedUserName = currentUserName?.trim();
  if (!trimmedReason || !trimmedUserName) {
    return reason;
  }

  const userSuffix = `(${trimmedUserName})`;
  return trimmedReason.includes(userSuffix) ? trimmedReason : `${trimmedReason} ${userSuffix}`;
}

export default function RankRow({
  label,
  rank,
  updated,
  overrideUpdated,
  edit,
  overrideOptions,
  currentUserName,
  onChange,
}: Props) {
  const hasOverride = Boolean(edit.override);
  const showOverrideReasonError = hasOverride && !edit.overrideReason.trim();

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={2}>
        <Typography fontWeight={600}>{label}</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          <TextField label="Rank" value={rank ?? '-'} disabled fullWidth />
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">
              Updated
            </Typography>
            <Typography>{formatDateTime(updated)}</Typography>
          </Box>
          <TextField
            select
            label="Override"
            value={edit.override}
            onChange={(event) => {
              const override = event.target.value;
              onChange({
                ...edit,
                override,
                overrideReason: override ? edit.overrideReason : '',
                overrideUntil: override ? edit.overrideUntil || dayjs().add(30, 'day').format('YYYY-MM-DD') : '',
              });
            }}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {overrideOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <DateWidget
            label={edit.overrideUntil ? 'Expire' : 'No expiry'}
            value={edit.overrideUntil}
            onChange={(overrideUntil) => onChange({ ...edit, overrideUntil })}
            disabled={!hasOverride}
            slotProps={{
              field: {
                clearable: Boolean(edit.overrideUntil),
                onClear: () => onChange({ ...edit, overrideUntil: '' }),
              },
            }}
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          <TextField
            label="Override reason"
            value={edit.overrideReason}
            onChange={(event) => onChange({ ...edit, overrideReason: event.target.value })}
            onBlur={() =>
              onChange({ ...edit, overrideReason: appendUserNameToReason(edit.overrideReason, currentUserName) })
            }
            disabled={!hasOverride}
            required={hasOverride}
            error={showOverrideReasonError}
            helperText={showOverrideReasonError ? 'Override reason is required' : undefined}
            fullWidth
          />
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">
              Override updated
            </Typography>
            <Typography>{formatDateTime(overrideUpdated)}</Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
